import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../../Services/firebase-config";
import { useAuth } from "../../../Contexts/AuthContext";

// Import icons from Heroicons
import {
    HomeIcon,
    UserGroupIcon,
    ClipboardListIcon,
    UserIcon,
    CogIcon,
    AcademicCapIcon,
    PlusIcon,
    MenuIcon,
    XIcon,
    TagIcon,
    CollectionIcon,
} from "@heroicons/react/outline";

const EventSideNav = ({ children }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const { currentUser, userData } = useAuth();

    // Get user role from AuthContext userData, check for admin/judge/mentor roles
    const userRole = userData?.role || "applicant";

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/");
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Define navigation items based on user role
    const getNavItems = () => {
        const baseItems = [];

        // Dashboard - Admin and Judge Page
        if (userRole === "Administrator" || userRole === "admin" || userRole === "Judge") {
            baseItems.push({ to: "/eventdashboard/dashboard", label: "Dashboard", icon: HomeIcon });
        }

        // Manage Applications - Admin, Judges and Mentors Page
        if (userRole === "Administrator" || userRole === "admin" || userRole === "Judge") {
            baseItems.push({ to: "/eventdashboard/manage-applications", label: "Manage Applications", icon: ClipboardListIcon });
        }

        // User Permissions - Admin Page Only
        if (userRole === "Administrator" || userRole === "admin") {
            baseItems.push({ to: "/eventdashboard/user-permissions", label: "User Permissions", icon: UserGroupIcon });
        }

        // Categories and Judges - Admin Page Only
        if (userRole === "Administrator" || userRole === "admin") {
            baseItems.push({ to: "/eventdashboard/categories-judges", label: "Categories & Judges", icon: TagIcon });
        }

        // Projects - Admin Page Only
        if (userRole === "Administrator" || userRole === "admin") {
            baseItems.push({ to: "/eventdashboard/projects-management", label: "Projects", icon: CollectionIcon });
        }

        // Teams Management - Admin, Judges and Mentors Page
        if (userRole === "Administrator" || userRole === "admin" || userRole === "Judge" || userRole === "Mentor") {
            baseItems.push({ to: "/eventdashboard/teams-management", label: "Teams Management", icon: UserGroupIcon });
        }

        // Mentor Assignment - Admin, Judges and Mentors Page
        if (userRole === "Administrator" || userRole === "admin" || userRole === "Judge" || userRole === "Mentor") {
            baseItems.push({ to: "/eventdashboard/mentor-assignment", label: "Mentor Assignment", icon: AcademicCapIcon });
        }

        // Mentor Dashboard - Mentor and Admin Page
        if (userRole === "Administrator" || userRole === "admin" || userRole === "Mentor") {
            baseItems.push({ to: "/eventdashboard/mentor-dashboard", label: "Mentor Dashboard", icon: AcademicCapIcon });
        }

        // Settings - Admin only
        if (userRole === "Administrator" || userRole === "admin") {
            baseItems.push({ to: "/eventdashboard/settings", label: "Settings", icon: CogIcon });
        }

        // Default items for other roles (if any)
        if (baseItems.length === 0) {
            return [
                { to: "/eventdashboard/dashboard", label: "Dashboard", icon: HomeIcon },
                { to: "/eventdashboard/profile", label: "Profile", icon: UserIcon },
            ];
        }

        return baseItems;
    };

    const navItems = getNavItems();

    return (
        <div className="fixed inset-0 bg-gray-50 overflow-hidden">
            {/* Mobile menu button */}
            <div className="fixed top-4 left-4 z-50 md:hidden">
                <button
                    onClick={toggleMobileMenu}
                    className="p-2.5 rounded-xl text-white bg-custom-blue-1 shadow-lg transition-all duration-200 hover:shadow-xl active:scale-95"
                >
                    {isMobileMenuOpen ? (
                        <XIcon className="h-5 w-5" />
                    ) : (
                        <MenuIcon className="h-5 w-5" />
                    )}
                </button>
            </div>

            {/* Mobile menu overlay */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black bg-opacity-60 md:hidden backdrop-blur-sm"
                    onClick={toggleMobileMenu}
                ></div>
            )}

            <div className="flex h-full">
                {/* Sidebar */}
                <aside
                    className={`fixed top-0 bottom-0 left-0 z-40 transform ${isCollapsed ? "w-16" : "w-64"
                        } bg-gradient-to-b from-custom-blue-1 to-custom-blue-1/90 text-white transition-all duration-300 ease-in-out md:translate-x-0 ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                        } shadow-2xl border-r border-white/10`}
                >
                    <div className="flex flex-col h-full">
                        {/* Header */}
                        <div className={`flex items-center ${isCollapsed ? "justify-center px-3" : "justify-between px-4"} py-4 border-b border-white/20 bg-white/5 backdrop-blur-sm`}>
                            <div className="flex items-center space-x-2">
                                <div className="relative">
                                    <img src="/Images/STIlogo.svg" alt="STI Logo" className="h-8 w-8 drop-shadow-lg" />
                                    <div className="absolute -inset-1 bg-white/20 rounded-full blur-sm -z-10"></div>
                                </div>
                                {!isCollapsed && (
                                    <div>
                                        <h1 className="text-lg font-bold tracking-tight">STI Portal</h1>
                                        <p className="text-xs text-white/70 font-medium">Innovation Hub</p>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={toggleCollapse}
                                className="hidden md:flex items-center justify-center w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                            >
                                {isCollapsed ? (
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                    </svg>
                                ) : (
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                    </svg>
                                )}
                            </button>
                        </div>

                        {/* User info */}
                        {!isCollapsed && (
                            <div className="px-4 py-3 border-b border-white/20 bg-white/5">
                                <div className="flex items-center space-x-3">
                                    <div className="relative">
                                        <div className="bg-gradient-to-br from-custom-green-1 to-green-400 p-2 rounded-lg shadow-lg">
                                            <UserIcon className="h-5 w-5 text-custom-blue-1" />
                                        </div>
                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow-sm"></div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-white truncate text-sm">
                                            {userData?.name || currentUser?.displayName || "User"}
                                        </p>
                                        <p className="text-xs text-white/80 capitalize font-medium">
                                            {userRole}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Navigation menu - Scrollable */}
                        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20 hover:scrollbar-thumb-white/30">
                            <nav className="px-3 py-3 space-y-1">
                                {navItems.map((item, index) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        className={({ isActive }) =>
                                            `group flex items-center ${isCollapsed ? "justify-center px-2" : "px-3"
                                            } py-2.5 rounded-lg transition-all duration-200 ${isActive
                                                ? "bg-custom-green-1 text-custom-blue-1 font-semibold shadow-lg transform scale-[1.02]"
                                                : "text-white/90 hover:bg-white/10 hover:text-white hover:shadow-md"
                                            }`
                                        }
                                    >
                                        <item.icon className={`h-5 w-5 transition-transform duration-200 group-hover:scale-110 ${isCollapsed ? "" : "mr-3"}`} />
                                        {!isCollapsed && (
                                            <span className="font-medium tracking-wide text-sm">{item.label}</span>
                                        )}
                                        {!isCollapsed && (
                                            <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                <div className="w-1.5 h-1.5 bg-current rounded-full"></div>
                                            </div>
                                        )}
                                    </NavLink>
                                ))}

                                {/* Admin quick actions */}
                                {userRole === "admin" && !isCollapsed && (
                                    <div className="mt-6 pt-4 border-t border-white/20">
                                        <h3 className="px-3 mb-3 text-xs font-bold text-white/60 uppercase tracking-wider">
                                            Quick Actions
                                        </h3>
                                        <div className="space-y-1">
                                            <button className="w-full group flex items-center px-3 py-2 text-xs rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 hover:shadow-md">
                                                <PlusIcon className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                                <span className="font-medium">Add Opportunity</span>
                                            </button>
                                            <button className="w-full group flex items-center px-3 py-2 text-xs rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 hover:shadow-md">
                                                <PlusIcon className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                                <span className="font-medium">Create Event</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </nav>
                        </div>

                        {/* Logout button */}
                        <div className="px-3 py-3 border-t border-white/20 bg-white/5">
                            <button
                                onClick={handleLogout}
                                className={`group w-full flex items-center ${isCollapsed ? "justify-center px-2" : "px-3"
                                    } py-2.5 rounded-lg text-white/90 hover:bg-red-500/20 hover:text-white transition-all duration-200 hover:shadow-md`}
                            >
                                <svg className={`h-5 w-5 transition-transform duration-200 group-hover:scale-110 ${isCollapsed ? "" : "mr-3"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                {!isCollapsed && <span className="font-medium text-sm">Logout</span>}
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Main content */}
                <div className={`flex-1 flex flex-col h-full transition-all duration-300 ${isMobileMenuOpen ? 'md:ml-64' : ''} ${isCollapsed ? "ml-0 md:ml-16" : "ml-0 md:ml-64"} min-w-0`}>

                    {/* Page content - Scrollable */}
                    <main className="flex-1 overflow-y-auto bg-gray-50 w-full">
                        <div className="p-3 md:p-4 pb-20 md:pb-6 min-h-full w-full max-w-full">
                            {children}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default EventSideNav;